import type {
  ButtonHTMLAttributes,
  ChangeEventHandler,
  PropsWithChildren,
} from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import {
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import type { RadioItemProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { useCommitmentPaymentPlanUpdate } from '@sola/my-sdk/commitments/hooks.ts';
import { getRecurringPaymentDay } from '@sola/my-sdk/utils.ts';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.ts';
import type {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { userErrorPaymentMethodAssignToCommitment } from '@sola/shared-utils/errors/supporters.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { paymentMethodsQuery } from '@sola/supporter-sdk/queries.ts';

import { LocalAlert } from '../../molecules/local-alerts/local-alerts.tsx';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
import type { LocalAlertConfig } from '../../types/localeAlerts.ts';
import { createSupportAlert } from '../../utils/alerts.tsx';
import { getErrorTranslationKeys } from '../../utils/errors.ts';
import { getPaymentMethodLabel } from '../../utils/payment-methods.ts';
import { PaymentMethodAddSheet } from '../payment-method-add-sheet/payment-method-add-sheet.tsx';

enum FormStatusEnum {
  Ok = 'Ok',
  Disabled = 'Disabled',
  Busy = 'Busy',
}

interface FormStatus {
  status: FormStatusEnum;
}

interface FormState extends FormStatus {
  isAddingNewMethod: boolean;
  selectedPaymentMethodId: GraphQLSupporterPaymentMethod['id'];
}

interface PaymentMethodSelectRadioGroupProps {
  selectedValue?: string | null;
  paymentMethods: GraphQLSupporterPaymentMethod[];
  onChange: ChangeEventHandler<HTMLInputElement>;
}

interface PaymentMethodSelectContentProps {
  selectedPaymentMethodId?: GraphQLSupporterPaymentMethod['id'];
  allowAddPaymentMethod?: boolean;
  alerts?: LocalAlertConfig[];
  removeAlert: (id: string) => void;
  // showSelectError: boolean;
  // onSelectErrorClose: () => void;
  onChange: ChangeEventHandler<HTMLInputElement>;
}

interface PaymentMethodSelectDialogProps {
  openTrigger: React.ReactElement;
  commitmentInternalId?: GraphQLCommitment['internalId'];
  currentPaymentMethodId?: GraphQLSupporterPaymentMethod['id'];
  currentPaymentDay?: number | null;
  allowAddPaymentMethod?: boolean;
  onSave: () => Promise<void>;
}

interface PaymentMethodSelectSubmitButtonProps
  extends
    PropsWithChildren,
    ButtonHTMLAttributes<HTMLButtonElement>,
    FormStatus {}

const addNewMethodValue = 'addMethod';

const getDefaultFormState = (
  currentPaymentMethodId?: GraphQLSupporterPaymentMethod['id'],
): FormState => ({
  status: currentPaymentMethodId ? FormStatusEnum.Ok : FormStatusEnum.Disabled,
  isAddingNewMethod: false,
  selectedPaymentMethodId: currentPaymentMethodId ?? '',
});

const useSelectContentData = () => {
  const { data, isLoading, isError } = useSupporterQuery(paymentMethodsQuery);

  return {
    isLoading,
    isError,
    paymentMethods: (data ?? []) as GraphQLSupporterPaymentMethod[],
  };
};

const PaymentMethodSelectRadioGroup: React.FC<
  PaymentMethodSelectRadioGroupProps &
    Pick<PaymentMethodSelectContentProps, 'allowAddPaymentMethod'>
> = ({ selectedValue, allowAddPaymentMethod, paymentMethods, onChange }) => {
  const { t } = useTranslation(['my', 'common']);
  const radioItems: RadioItemProps[] = paymentMethods
    .filter(({ id }) => id?.length)
    .map((paymentMethod) => {
      const { id, accountNickname } = paymentMethod;
      return {
        text: getPaymentMethodLabel(paymentMethod, t),
        value: id as string,
        ...(accountNickname && {
          badge: {
            label: accountNickname,
            size: 'sm',
          },
        }),
      };
    });

  return (
    <RadioGroup
      aria-label={t('paymentMethods.selectDialog.title')}
      items={
        allowAddPaymentMethod
          ? [
              ...radioItems,
              {
                text: t('paymentMethods.selectDialog.addNewRadioLabel'),
                value: addNewMethodValue,
              },
            ]
          : radioItems
      }
      name="paymentMethod"
      onChange={onChange}
      required
      value={selectedValue}
    />
  );
};

const PaymentMethodSelectContent: React.FC<PaymentMethodSelectContentProps> = ({
  selectedPaymentMethodId,
  allowAddPaymentMethod,
  onChange,
  alerts,
  removeAlert,
}) => {
  const { t } = useTranslation(['my', 'core']);
  const { paymentMethods, isLoading, isError } = useSelectContentData();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-comfy-sm">
        <Skeleton height={30} width={300} />
        <Skeleton height={30} width={300} />
        <Skeleton height={30} width={300} />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        variant="negative"
        title={t('paymentMethods.queryError.title', { count: 0 })}
        message={t('paymentMethods.queryError.message')}
        action={
          <a href={t('ctas.contactSupport.url')}>
            {t('ctas.contactSupport.text')}
          </a>
        }
      />
    );
  }

  return (
    <>
      {alerts?.map((alert) => (
        <LocalAlert
          key={alert.id}
          className="mb-comfy-xl"
          alert={alert}
          onClose={removeAlert}
        />
      ))}

      <PaymentMethodSelectRadioGroup
        selectedValue={selectedPaymentMethodId}
        allowAddPaymentMethod={allowAddPaymentMethod}
        paymentMethods={paymentMethods}
        onChange={onChange}
      />
    </>
  );
};

const useComponentData = ({
  commitmentInternalId,
  currentPaymentMethodId,
  currentPaymentDay,
  onSave,
}: {
  commitmentInternalId?: GraphQLCommitment['internalId'];
  currentPaymentMethodId?: GraphQLCommitment['paymentMethodId'];
  currentPaymentDay?: GraphQLCommitment['paymentPullDay'];
  onSave: () => Promise<void>;
}) => {
  const paymentPullDay =
    currentPaymentDay || getRecurringPaymentDay(new Date().getDate());
  const { alerts, setAlerts, clearAlerts, removeAlert } = useModalAlerts();
  const paymentPlanUpdate =
    useCommitmentPaymentPlanUpdate(commitmentInternalId);

  const [formState, setFormState] = useState<FormState>(
    getDefaultFormState(currentPaymentMethodId),
  );

  const [isOpen, setIsOpen] = useState(false);

  const { status, isAddingNewMethod, selectedPaymentMethodId } = formState;

  const handleOpen = (open: boolean) => {
    setFormState(getDefaultFormState(currentPaymentMethodId));
    if (!open) {
      clearAlerts();
    }
    setIsOpen(open);
  };

  const handleChangePaymentMethod: ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    if (!e.target.value) {
      setFormState(getDefaultFormState(currentPaymentMethodId));
      return;
    }

    if (e.target.value === addNewMethodValue) {
      setFormState({
        status: FormStatusEnum.Ok,
        isAddingNewMethod: true,
        selectedPaymentMethodId: e.target.value,
      });
      return;
    }

    setFormState({
      status: FormStatusEnum.Ok,
      isAddingNewMethod: false,
      selectedPaymentMethodId: e.target.value,
    });
  };

  const handleSave = async (
    paymentMethodId?: GraphQLSupporterPaymentMethod['id'],
  ) => {
    setFormState((currentState) => ({
      ...currentState,
      status: FormStatusEnum.Busy,
    }));
    clearAlerts();

    try {
      const { success } = await paymentPlanUpdate.mutateAsync({
        newPaymentMethodId: paymentMethodId,
        paymentDay: paymentPullDay,
      });

      if (!success) {
        throw userErrorPaymentMethodAssignToCommitment();
      }

      if (onSave) {
        try {
          await onSave();
        } catch {
          // Separate try-catch block so a thrown error in onSave doesn't trigger paymentPlanUpdate error
        }
      }

      setIsOpen(false);
    } catch (error) {
      setFormState({
        isAddingNewMethod: false,
        selectedPaymentMethodId: paymentMethodId,
        status: FormStatusEnum.Ok,
      });

      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodAssignToCommitment,
        error,
      );
      setAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
  };

  const handleAdd = async (paymentMethod: GraphQLSupporterPaymentMethod) => {
    if (!paymentMethod) {
      return;
    }

    await handleSave(paymentMethod.id);
  };

  return {
    paymentPullDay,
    alerts,
    removeAlert,
    formState,
    isOpen,
    status,
    isAddingNewMethod,
    selectedPaymentMethodId,
    handleChangePaymentMethod,
    handleOpen,
    handleSave,
    handleAdd,
  };
};

const PaymentMethodSelectSubmitButton: React.FC<
  PaymentMethodSelectSubmitButtonProps
> = ({ children, status, ...buttonProps }) => (
  <ButtonStyle
    className="w-full md:w-fit"
    disabled={status !== FormStatusEnum.Ok}
    isBusy={status === FormStatusEnum.Busy}
  >
    <button type="submit" {...buttonProps}>
      {children}
    </button>
  </ButtonStyle>
);

export const PaymentMethodSelectDialog: React.FC<
  PaymentMethodSelectDialogProps
> = ({
  openTrigger,
  commitmentInternalId,
  currentPaymentMethodId,
  currentPaymentDay,
  allowAddPaymentMethod = false,
  onSave,
}) => {
  const { t } = useTranslation(['my', 'core']);
  const {
    paymentPullDay,
    alerts,
    removeAlert,
    formState,
    isOpen,
    status,
    isAddingNewMethod,
    selectedPaymentMethodId,
    handleChangePaymentMethod,
    handleOpen,
    handleSave,
    handleAdd,
  } = useComponentData({
    commitmentInternalId,
    currentPaymentMethodId,
    currentPaymentDay,
    onSave,
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleOpen}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent
        icon="info-circle"
        title={t('paymentMethods.selectDialog.title')}
        supportingText={t('paymentMethods.selectDialog.supportingText')}
      >
        <PaymentMethodSelectContent
          selectedPaymentMethodId={formState.selectedPaymentMethodId}
          allowAddPaymentMethod={allowAddPaymentMethod}
          alerts={alerts}
          removeAlert={removeAlert}
          // showSelectError={isShowingSelectError}
          // onSelectErrorClose={() => setIsShowingSelectError(false)}
          onChange={handleChangePaymentMethod}
        />

        <Divider purpose="neutral" className="my-comfy-xl" />

        {paymentPullDay && paymentPullDay > 0 && (
          <div className="label-lg label-neutral-mild pb-comfy-sm">
            <span className="label-potent">
              {t('commitments.details.paymentDay.label')}{' '}
            </span>
            {t('commitments.details.paymentDay.paymentPullDayLabel', {
              count: paymentPullDay,
              ordinal: true,
            })}
          </div>
        )}

        <DialogActions>
          <ButtonStyle
            className="w-full md:w-fit"
            size="md"
            disabled={status === FormStatusEnum.Busy}
            purpose="neutral"
            variant="secondary"
          >
            <DialogClose>{t('core:actions.cancel')}</DialogClose>
          </ButtonStyle>
          {isAddingNewMethod ? (
            <PaymentMethodAddSheet
              onComplete={handleAdd}
              openTrigger={
                <PaymentMethodSelectSubmitButton status={status}>
                  {t('core:actions.continue')}
                </PaymentMethodSelectSubmitButton>
              }
            />
          ) : (
            <PaymentMethodSelectSubmitButton
              onClick={async () => await handleSave(selectedPaymentMethodId)}
              status={status}
            >
              {t('core:actions.save')}
            </PaymentMethodSelectSubmitButton>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
